/***********************************
 *  Grade4MathLesson.js  (本地 user + Range Safe)
 *  100% localStorage 取 user，防止 NaN/空頁
 ***********************************/

import React, { useState, useEffect, useRef, useMemo } from "react";
import "../Grade4/Grade4MathLesson.css";
import Calculator from "../MathTools/Calculator.js";
import WritingPad from "../MathTools/WritingPad.js";
import drawGraphOnCanvas from "../MathTools/drawGraph.js";
import draw3DShapeOnCanvas from "../MathTools/draw3DShape.js";
import ProgressBar from "../MathPagesStructure/ProgressBar.js";
import LessonUI from "../MathPagesStructure/LessonUI.js";
import {
  StoryModeQuiz,
  QuizResultScreen,
  FinalQuizUI,
} from "../MathPagesStructure/StoryModeStructure.js";
import {
  InteractiveGraphSection,
  drawShapeOnCanvas,
  generateQuizQuestion,
} from "./Grade4QuizGenerator.js";
import { mathChapterPages } from "../MathLessonPools";

// 1. 定義章節與範圍
const chapterPageRanges = [
  [0, 6],
  [7, 8],
  [9, 10],
  [11, 14],
  [15, 17],
  [18, 19]
];
const chapterTopicMap = {
  1: "numberOps",
  2: "patternsAlgebra",
  3: "measurement",
  4: "geometry",
  5: "dataProbability",
  6: "financialLit",
};
const SHAPES_2D = ["Triangle", "Square", "Circle", "Pentagon", "Hexagon"];
const SHAPES_3D = [
  "Cube",
  "Rectangular Prism",
  "Pyramid",
  "Triangular Prism",
  "Cylinder",
  "Sphere",
];

export default function Grade4MathLesson({ onAwardTokens }) {
  // ---- 本地 user 取 localStorage
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("loggedInUser");
    return stored ? JSON.parse(stored) : null;
  });

  // ---- 章節 & 畫面進度
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);
  const [chapDone, setChapDone] = useState(false);
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);

  // ---- Quiz 狀態
  const [quiz, setQuiz] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizFin, setQuizFin] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  // ---- Final Quiz 狀態
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fFin, setFFin] = useState(false);
  const [finalTime, setFinalTime] = useState(300);

  // ---- Demo shape
  const [shapeIdx, setShapeIdx] = useState(0);
  const [solidIdx, setSolidIdx] = useState(0);
  const nextShape = () => setShapeIdx(i => (i + 1) % SHAPES_2D.length);
  const nextSolid = () => setSolidIdx(i => (i + 1) % SHAPES_3D.length);
  const graphRef = useRef(null);
  const canvas2DRef = useRef(null);
  const canvas3DRef = useRef(null);

  // ---- 工具面板
  const [showCalc, setShowCalc] = useState(false);
  const [showPad, setShowPad] = useState(false);

  // ---- 過關判斷
  useEffect(() => {
    const done = localStorage.getItem("grade4MathComplete");
    if (done === "true") {
      setHasCompletedBefore(true);
      setChapDone(true);
    }
  }, []);
  useEffect(() => {
    if (chapDone) localStorage.setItem("grade4MathComplete", "true");
  }, [chapDone]);

  // ---- Chapter Pages Range Safe
  const [startIdx, endIdx] = chapterPageRanges[chapter - 1] || [0, 0];
  const currentPages = mathChapterPages[4]?.slice(startIdx, (endIdx ?? 0) + 1) || [];

  // ---- 畫圖 Demo
  useEffect(() => {
    if (chapter === 4 && currentPages[pageIdx]?.isShapeDemo) {
      drawShapeOnCanvas(canvas2DRef.current, shapeIdx);
      draw3DShapeOnCanvas(canvas3DRef.current, solidIdx);
    }
  }, [chapter, pageIdx, currentPages, shapeIdx, solidIdx]);
  useEffect(() => {
    if (chapter === 5 && currentPages[pageIdx]?.isGraphDemo) {
      drawGraphOnCanvas(graphRef.current, [
        { label: "Dog", value: 4 },
        { label: "Cat", value: 3 },
        { label: "Fish", value: 2 },
        { label: "Bird", value: 1 },
      ]);
    }
  }, [chapter, pageIdx, currentPages]);

  // ---- Progress Bar Safe
  const totalSlides = useMemo(() => {
    let total = 0;
    for (const [s, e] of chapterPageRanges) total += (e - s + 1);
    return total;
  }, []);
  const slidesViewed = useMemo(() => {
    let acc = 0;
    for (let i = 0; i < chapter - 1; i++) {
      const [s, e] = chapterPageRanges[i];
      acc += e - s + 1;
    }
    return acc + pageIdx;
  }, [chapter, pageIdx]);
  const progress = totalSlides ? Math.round(((slidesViewed + 1) / totalSlides) * 100) : 0;

  // ---- Quiz helpers
  function generateChapterQuiz(n = 10) {
    const topic = chapterTopicMap[chapter];
    return Array.from({ length: n }, () => generateQuizQuestion(topic));
  }
  function startChapterQuiz() {
    setQuiz(generateChapterQuiz());
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(true);
  }
  function handleQuizAnswer(opt) {
    if (opt === quiz[qIdx].correctAnswer) setQScore(s => s + 1);
    if (qIdx < quiz.length - 1) setQIdx(i => i + 1);
    else setQuizFin(true);
  }
  function finishChapterQuiz() {
    if (qScore >= 5) {
      if (chapter < 6) {
        setChapter(c => c + 1);
        setPageIdx(0);
        setShowQuiz(false);
        setQuizFin(false);
      } else {
        setChapDone(true);
        setShowQuiz(false);
      }
    } else {
      alert("Below 50 %. Please review and retry!");
      startChapterQuiz();
    }
  }

  // ---- Final Quiz helpers
  function generateFinalQuiz() {
    const seen = new Set();
    const arr = [];
    while (arr.length < 50) {
      const q = generateQuizQuestion();
      const key = `${q.question}-${q.correctAnswer}`;
      if (!seen.has(key)) {
        seen.add(key);
        arr.push(q);
      }
    }
    return arr;
  }
  function startFinal() {
    setFinalQs(generateFinalQuiz());
    setFIdx(0);
    setFScore(0);
    setFFin(false);
    setFinalTime(300);
    setShowFinal(true);
  }
  function handleFinalAns(opt) {
    if (opt === finalQs[fIdx].correctAnswer) setFScore(s => s + 1);
    if (fIdx < finalQs.length - 1) setFIdx(i => i + 1);
    else setFFin(true);
  }
  useEffect(() => {
    if (!showFinal || fFin) return;
    if (finalTime === 0) {
      setFFin(true);
      return;
    }
    const tid = setTimeout(() => setFinalTime(t => t - 1), 1000);
    return () => clearTimeout(tid);
  }, [showFinal, fFin, finalTime]);

  // ---- Award tokens for passing final
  const hasAwardedTokens = useRef(false);
  useEffect(() => {
    if (!fFin || hasAwardedTokens.current) return;
    if ((fScore / finalQs.length) * 100 >= 80) {
      if (onAwardTokens) onAwardTokens(1);
      hasAwardedTokens.current = true;
    }
  }, [fFin, fScore, finalQs.length, onAwardTokens]);

  // ---- Avatar：localStorage 取 user.avatar（完全比照 Grade3）
  const studentAvatar =
    user?.avatar ||
    user?.photoURL ||
    `${process.env.PUBLIC_URL}/images/Student.png`;

  const quizUser = {
    ...user,
    avatar: studentAvatar,
    profilePic: studentAvatar,
  };

  // ---- UI
  const ChapterUI = () => {
    if (!currentPages.length) return <div>No content for this chapter.</div>;
    if (showQuiz && !quizFin) {
      return (
        <StoryModeQuiz
          questions={quiz}
          onAnswer={handleQuizAnswer}
          currentIndex={qIdx}
          score={qScore}
          user={quizUser}
          showCalc={showCalc}
          showPad={showPad}
          onToggleCalc={() => setShowCalc(v => !v)}
          onTogglePad={() => setShowPad(v => !v)}
          tools={
            <>
              {showCalc && <Calculator onClose={() => setShowCalc(false)} />}
              {showPad && <WritingPad onClose={() => setShowPad(false)} />}
            </>
          }
        />
      );
    }
    if (showQuiz && quizFin) {
      return (
        <QuizResultScreen
          score={qScore}
          total={quiz.length}
          onContinue={finishChapterQuiz}
        />
      );
    }
    return (
      <LessonUI
        pages={currentPages}
        currentPageIndex={pageIdx}
        onPrevPage={() => setPageIdx(p => p - 1)}
        onNextPage={() => setPageIdx(p => p + 1)}
        onStartQuiz={startChapterQuiz}
        backTo="/math"
        renderExtraSection={({ page }) => {
          if (chapter === 5 && page.isGraphDemo) {
            return (
              <InteractiveGraphSection
                graphCanvasRef={graphRef}
                initialData={[
                  { label: "Dog", value: 4 },
                  { label: "Cat", value: 3 },
                  { label: "Fish", value: 2 },
                  { label: "Bird", value: 1 },
                ]}
              />
            );
          }
          if (chapter === 4 && page.isShapeDemo) {
            return (
              <>
                <div className="shape-container">
                  <canvas
                    ref={canvas2DRef}
                    width="200"
                    height="140"
                    className="shapes-canvas"
                  />
                  <p className="shape-name">{SHAPES_2D[shapeIdx]}</p>
                </div>
                <button className="button1" onClick={nextShape}>
                  Draw Next 2D Shape
                </button>
                <div className="shape-container" style={{ marginTop: 20 }}>
                  <canvas
                    ref={canvas3DRef}
                    width="200"
                    height="200"
                    className="shapes-canvas"
                  />
                  <p className="shape-name">{SHAPES_3D[solidIdx]}</p>
                </div>
                <button className="button1" onClick={nextSolid}>
                  Draw Next 3D Shape
                </button>
              </>
            );
          }
          return null;
        }}
      />
    );
  };

  return (
    <div className="grade4-math-page">
      <div className="grade4-math-page-scale">
        <ProgressBar progress={progress} />
        {/* quick‑jump panel */}
        {hasCompletedBefore && !showFinal && !showQuiz && (
          <div style={{ margin: "15px 0", textAlign: "center", color: "#555" }}>
            <p style={{ marginBottom: 10 }}>
              You previously completed all Grade 4 lessons. You can jump to any chapter or retake the final quiz for practice.
            </p>
            <div>
              {[1, 2, 3, 4, 5, 6].map(n => (
                <button key={n} style={{ marginRight: 8 }} onClick={() => {
                  setChapter(n);
                  setPageIdx(0);
                  setShowQuiz(false);
                  setShowFinal(false);
                }}>
                  Go to Chapter {n}
                </button>
              ))}
              <button onClick={startFinal}>Go to Final Quiz</button>
            </div>
          </div>
        )}

        {/* Main area */}
        {!chapDone && !showFinal && <ChapterUI />}
        {chapDone && !showFinal && (
          <div style={{ textAlign: "center" }}>
            <h3>All Chapters Completed!</h3>
            <button className="button1" onClick={startFinal}>
              Start Final Quiz
            </button>
          </div>
        )}

        {showFinal && (
          <FinalQuizUI
            questions={finalQs}
            currentIndex={fIdx}
            score={fScore}
            timeLeft={finalTime}
            user={quizUser}
            onAnswer={handleFinalAns}
            quizFinished={fFin}
            onReturn={() => setShowFinal(false)}
            resultMsg={user && (
              <p>
                You now have <strong>{user.tokens ?? 0}</strong> tokens!
              </p>
            )}
          />
        )}
      </div>
    </div>
  );
}
